import React from 'react';
import { FiArrowRight } from 'react-icons/fi';
import { FaGraduationCap } from 'react-icons/fa';
import {
  Main,
  MaxWidth,
  Title,
  Heading,
  Timeline,
  Spine,
  SpineFill,
  Row,
  Node,
  Card,
  Logo,
  Info,
  Role,
  Company,
  Kind,
  Dates,
} from './Experience.elements';
import useInView from '../../useInView';

const L = `${process.env.PUBLIC_URL}/timeline_logos`;

// type: 'work' | 'education' — ordered newest start first
const TIMELINE = [
  { type: 'education', org: 'McKinsey & Company', role: 'McKinsey Forward Program', from: 'Apr 2026', to: 'Now', logo: `${L}/mckinsey_logo.jpeg` },
  { type: 'education', org: 'Tilburg University', role: 'Erasmus — Business & Technology', from: 'Jan 2026', to: 'Aug 2026', logo: `${L}/tilburg_university.png` },
  { type: 'work', org: 'BosNets', role: 'Software Developer & Researcher', from: 'Oct 2025', to: 'Now', logo: `${L}/bosnets.png` },
  { type: 'education', org: 'Özyeğin University', role: 'Management Information Systems', from: 'Sep 2025', to: 'Jun 2028', logo: `${L}/ozyegin_university.png` },
  { type: 'work', org: 'Nailing.ai', role: 'Co-Founder & Product Engineer', from: 'Jul 2025', to: 'Now', logo: `${L}/nailing.png` },
  { type: 'work', org: 'Vialess', role: 'Software Developer', from: 'Jul 2025', to: 'Oct 2025', logo: `${L}/vialess.jpeg` },
  { type: 'work', org: 'AVIDEF', role: 'Software Developer & Business Analyst', from: 'Nov 2024', to: 'Now', logo: `${L}/avidef.png` },
  { type: 'education', org: 'Özyeğin University', role: 'Business Administration (100% Scholarship)', from: 'Aug 2023', to: 'Jun 2028', logo: `${L}/ozyegin_university.png` },
  { type: 'work', org: 'Wugo', role: 'Software Developer', from: 'Jul 2023', to: 'Feb 2026', logo: `${L}/wugo.jpeg` },
  { type: 'work', org: '4Ps Networks', role: 'Full-Stack Developer', from: 'Mar 2022', to: 'Sep 2022', logo: `${L}/4ps.jpeg` },
  { type: 'work', org: 'New Mind', role: 'Software Development Intern', from: 'Jun 2021', to: 'Dec 2021', logo: `${L}/newmind_ai_logo.jpeg` },
  { type: 'work', org: 'Freelance', role: 'Software Developer', from: 'Mar 2020', to: 'Jul 2023', logo: null },
  { type: 'education', org: 'Beyoğlu Anatolian İHL', role: 'High School — Grade 97.07', from: 'Sep 2018', to: 'Jun 2023', logo: `${L}/high_school.jpeg` },
];

const headVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: 0.1 * i, ease: 'easeOut' },
  }),
};

function TimelineRow({ item, index }) {
  const side = index % 2 === 0 ? 'left' : 'right';
  const isEdu = item.type === 'education';
  const [ref, inView] = useInView({ amount: 0.35 });

  const cardVariants = {
    hidden: { opacity: 0, x: side === 'left' ? -60 : 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const nodeVariants = {
    hidden: { scale: 0 },
    visible: { scale: 1, transition: { duration: 0.35, delay: 0.15, ease: 'backOut' } },
  };

  return (
    <Row ref={ref} $side={side}>
      <Node
        variants={nodeVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      />
      <Card
        variants={cardVariants}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
      >
        <Logo>
          {item.logo ? (
            <img src={item.logo} alt={`${item.org} logo`} />
          ) : isEdu ? (
            <FaGraduationCap />
          ) : (
            item.org.charAt(0)
          )}
        </Logo>
        <Info>
          <Role>{item.role}</Role>
          <Company>
            {item.org}
            <Kind>{isEdu ? 'Education' : 'Work'}</Kind>
          </Company>
        </Info>
        <Dates>
          {item.from}
          <FiArrowRight />
          <span className={item.to === 'Now' ? 'now' : ''}>{item.to}</span>
        </Dates>
      </Card>
    </Row>
  );
}

function Experience() {
  const [spineRef, spineInView] = useInView({ amount: 0.05 });

  return (
    <Main id="experience">
      <MaxWidth>
        <Title variants={headVariants} initial="hidden" animate="visible">
          Where I&apos;ve been
        </Title>
        <Heading variants={headVariants} custom={1} initial="hidden" animate="visible">
          Experience &amp; Education
        </Heading>

        <Timeline ref={spineRef}>
          <Spine>
            <SpineFill
              initial={{ scaleY: 0 }}
              animate={{ scaleY: spineInView ? 1 : 0 }}
              transition={{ duration: 1.1, ease: 'easeInOut' }}
            />
          </Spine>

          {TIMELINE.map((item, i) => (
            <TimelineRow key={item.org + item.from} item={item} index={i} />
          ))}
        </Timeline>
      </MaxWidth>
    </Main>
  );
}

export default Experience;
